import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import * as Sentry from "@sentry/react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Session keep-alive: while signed in, quietly re-verify (which slides the server-side
  // session expiry) every few minutes and whenever the tab regains focus, so active users
  // are almost never bounced to sign-in mid-work.
  useEffect(() => {
    if (!user) return;
    const KEEPALIVE_MS = 5 * 60 * 1000;
    const ping = () => { checkAuth(); };
    const id = setInterval(ping, KEEPALIVE_MS);
    const onVisible = () => { if (document.visibilityState === "visible") ping(); };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, checkAuth]);

  // Tag Sentry events with the signed-in user for easier support triage
  // (Sentry.setUser is a safe no-op when Sentry isn't configured).
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.user_id, email: user.email, username: user.name });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error("Logout request failed:", e);
    }
    setUser(null);
    window.location.href = "/login";
  };

  const value = useMemo(() => ({ user, setUser, loading, checkAuth, logout }), [user, loading, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
